/**
 * FounderTrackRedirect (UX-1, 2026-05-16)
 *
 * Wraps the bare /dashboard route + the /welcome route. If the
 * signed-in user has a primary user_active_dens row, redirects to
 * the matching Den surface.
 *
 * 2026-05-16 (DASHBOARD-REDIRECT-FIX): two bugs fixed.
 *   (1) Order column was `created_at` but the table only has
 *       `added_at`. The bad query 400'd, the catch fell to
 *       passthrough, and founder users saw the welcome-inbox
 *       cards flash before any redirect could fire.
 *   (2) Loading rendered children — which meant Welcome.tsx
 *       rendered for ~200-800ms before the redirect kicked in.
 *       Now we render a tiny inline spinner during the resolve
 *       so there's no wrong-page flash. ~200ms blank-with-spinner
 *       is far less jarring than landing on the wrong surface and
 *       then snapping away.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: ReactNode;
}

type Decision = "loading" | "redirect_founder" | "redirect_investor" | "passthrough";

export function FounderTrackRedirect({ children }: Props) {
  const [decision, setDecision] = useState<Decision>("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (alive) setDecision("passthrough");
          return;
        }
        const { data: dens } = await supabase
          .from("user_active_dens" as any)
          .select("persona_key, is_primary, added_at")
          .eq("user_id", user.id)
          .order("is_primary", { ascending: false })
          .order("added_at", { ascending: false })
          .limit(5);
        const rows = (dens || []) as Array<{ persona_key: string; is_primary: boolean | null }>;
        const primary = rows.find(r => r.is_primary) || rows[0];
        if (!alive) return;
        if (primary?.persona_key === "founder") setDecision("redirect_founder");
        else if (primary?.persona_key === "investor") setDecision("redirect_investor");
        else setDecision("passthrough");
      } catch {
        if (alive) setDecision("passthrough");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (decision === "redirect_founder") return <Navigate to="/dashboard/my-den" replace />;
  if (decision === "redirect_investor") return <Navigate to="/dashboard/investor-den" replace />;
  if (decision === "loading") {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
          <p className="text-xs text-muted-foreground">Taking you to your Den…</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export default FounderTrackRedirect;
